import ReactQueryProvider from "@/providers/ReactQuery";

import { Box } from "@mui/material";
import { useEffect } from "react";
import { Helmet } from "react-helmet";
import {
  Route,
  BrowserRouter as Router,
  Routes,
  useLocation,
} from "react-router-dom";

import MusicPlayer from "@/components/MusicPlayer";
import FooterLinks from "@/fragments/FooterLinks";
import ReleaseDate from "@/fragments/ReleaseDate";
import About from "@/pages/About";
import Accounts from "@/pages/Accounts";
import AdminConsole from "@/pages/AdminConsole";
import Artist from "@/pages/Artist";
import ArtistAdmin from "@/pages/ArtistAdmin";
import ArtistData from "@/pages/ArtistData";
import ArtistDisplay from "@/pages/ArtistDisplay";
import ArtistSongs from "@/pages/ArtistSongs";
import Blocked from "@/pages/Blocked";
import Contact from "@/pages/Contact";
import Home from "@/pages/Home";
import Live from "@/pages/Live";
import Login from "@/pages/Login";
import Logout from "@/pages/Logout";
import ManageExperience from "@/pages/ManageExperience";
import Media from "@/pages/Media";
import Moderation from "@/pages/Moderation";
import Music from "@/pages/Music";
import Notes from "@/pages/Notes";
import Post from "@/pages/Post";
import Privacy from "@/pages/Privacy";
import Refund from "@/pages/Refund";
import Scheduled from "@/pages/Scheduled";
import Store from "@/pages/Store";
import Terms from "@/pages/Terms";
import Threads from "@/pages/Threads";
import UserAccount from "@/pages/UserAccount";

import TermsUpdateBanner from "@/components/TermsUpdateBanner";
import { DraftPostProvider } from "@/contexts/DraftPostContext";
import { MusicProvider } from "@/contexts/MusicContext";
import { redeemStoredInvite } from "@/services/auth";
import { fetchCurrentUser, fetchLocation } from "@/services/api";
import { getCurrentUser, getPageTitle } from "@/utils";

export const currentUser = getCurrentUser();

export default function App() {
  useEffect(() => {
    if (currentUser) {
      fetchCurrentUser();
      // already-logged-in users never hit verify/createUser, so redeem any
      // invite captured from ?invite= here too
      redeemStoredInvite();
    }

    if (
      currentUser &&
      (localStorage.getItem("loc") == null ||
        localStorage.getItem("country") == null ||
        localStorage.getItem("limited") == null)
    ) {
      // fire-and-forget; on success it dispatches "location-updated" so gated
      // UI (SubscribedProvider, music gate) refreshes without a manual reload
      fetchLocation().catch((error) =>
        console.error("Error fetching location:", error),
      );
    }
  }, [currentUser]);

  // if code in params, save to local storage
  const params = new URLSearchParams(window.location.search);
  const code = params.get("code");
  if (code) {
    console.log("code", code);
    localStorage.setItem("code", code);
  }

  // if invite in params, save to local storage (free invite flow, distinct from
  // the Stripe coupon `code`)
  const invite = params.get("invite");
  if (invite) {
    localStorage.setItem("invite", invite);
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100dvh" }}>
      <ReactQueryProvider>
        <Router>
          <Helmet>
            <title>{getPageTitle()}</title>
          </Helmet>
          <TermsUpdateBanner />
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
            <MusicProvider>
              <DraftPostProvider>
                <Routes>
                  <Route path="/blocked" element={<Blocked />} />
                  <Route path="/terms" element={<Terms />} />
                  <Route path="/privacy" element={<Privacy />} />
                  <Route path="/refund" element={<Refund />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/logout" element={<Logout />} />

                  {currentUser?.admin && (
                    <>
                      <Route path="/console" element={<AdminConsole />} />
                    </>
                  )}

                  {currentUser?.role == "artist" && (
                    <>
                      {/* could /artist/prefix this */}
                      <Route path="/account" element={<ArtistDisplay />} />
                      <Route path="/admin" element={<ArtistAdmin />} />
                      <Route path="/data" element={<ArtistData />} />
                      <Route path="/moderation" element={<Moderation />} />
                      <Route path="/post" element={<Post />} />
                      <Route path="/scheduled" element={<Scheduled />} />
                      <Route path="/songs" element={<ArtistSongs />} />
                      <Route
                        path="/experience/:key"
                        element={<ManageExperience />}
                      />
                    </>
                  )}

                  {!currentUser && (
                    <>
                      <Route path="/:artistKey" element={<Login />} />
                      <Route path="/" element={<Login />} />
                    </>
                  )}

                  {currentUser && (
                    <>
                      <Route path="/:artistKey" element={<Artist />} />
                      <Route path="/:artistKey/notes" element={<Notes />} />
                      <Route path="/:artistKey/music" element={<Music />} />
                      <Route path="/:artistKey/threads" element={<Threads />} />
                      <Route path="/:artistKey/store" element={<Store />} />
                      <Route path="/:artistKey/media" element={<Media />} />
                      <Route path="/:artistKey/live" element={<Live />} />

                      <Route path="/live" element={<Live />} />
                      <Route path="/account" element={<UserAccount />} />
                      <Route path="/accounts" element={<Accounts />} />

                      <Route path="/subscriptions" element={<Home />} />
                      <Route path="/" element={<Home />} />
                    </>
                  )}
                </Routes>

                <MusicPlayer />
              </DraftPostProvider>
            </MusicProvider>
          </Box>
          <SiteFooter />
        </Router>
      </ReactQueryProvider>
    </Box>
  );
}

function SiteFooter() {
  const { pathname } = useLocation();
  if (pathname.endsWith("/live")) return null;
  return (
    <Box
      sx={{
        flexDirection: "column",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        pb: { xs: 12, lg: 2 },
      }}
    >
      <FooterLinks />
      <ReleaseDate />
    </Box>
  );
}
